import { useState, useEffect, useRef } from "react"
import {
  X,
  ChevronDown,
  Pencil,
  Bold,
  Italic,
  Underline,
  Link,
  Image,
  Code,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import { useUser } from "../contexts/UserContext"
import { collection, getDocs, orderBy, query, where } from "firebase/firestore"
import { db } from "../services/firebase"
import { sendLeadEmail } from "../services/messageService"

const ff =
  "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const divider = "1px solid #f3f4f6"

// ── Helpers ───────────────────────────────────────────────────────────────────

const toast = {
  error: (msg) => console.error("Error:", msg),
  success: (msg) => console.log("Success:", msg),
}

const resolvePlaceholders = (template, ctx) => {
  if (!ctx || !template) return template ?? ""
  return template.replace(/\{\{([^}]+)\}\}/g, (_, path) => {
    const value = path.split(".").reduce((obj, key) => obj?.[key], ctx)
    return value ?? `{{${path}}}`
  })
}

const getTextContent = (html) => {
  const div = document.createElement("div")
  div.innerHTML = html
  return div.textContent || div.innerText || ""
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

const useEmailTemplates = () => {
  const [templates, setTemplates] = useState([])
  const { organizationId } = useUser()

  useEffect(() => {
    if (!organizationId) return
    const load = async () => {
      try {
        const q = query(
          collection(db, "emailTemplates"),
          where("organizationId", "==", organizationId)
        )
        const snap = await getDocs(q)
        setTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      } catch (err) {
        console.error("Failed to load templates:", err)
      }
    }
    load()
  }, [organizationId])
  return templates
}

const useOutsideClick = (cb) => {
  const ref = useRef(null)
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) cb()
    }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [cb])
  return ref
}

// ── Toolbar ───────────────────────────────────────────────────────────────────

const ToolbarButton = ({ title, onClick, children, active }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => {
      e.preventDefault()
      onClick?.()
    }}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      border: "none",
      borderRadius: 5,
      background: active ? "#f3f4f6" : "none",
      color: active ? "#111827" : "#374151",
      cursor: "pointer",
      flexShrink: 0,
      transition: "background 0.1s",
    }}
    onMouseEnter={(e) => {
      if (!active) e.currentTarget.style.background = "#f3f4f6"
    }}
    onMouseLeave={(e) => {
      if (!active) e.currentTarget.style.background = "none"
    }}
  >
    {children}
  </button>
)

const ColorSwatch = ({ color, onSelect }) => (
  <button
    type="button"
    onMouseDown={(e) => {
      e.preventDefault()
      onSelect(color)
    }}
    style={{
      width: 16,
      height: 16,
      borderRadius: 3,
      background: color,
      border: "1px solid rgba(0,0,0,0.12)",
      cursor: "pointer",
      flexShrink: 0,
    }}
  />
)

const FONT_COLORS = [
  "#111827",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
]

const EditorToolbar = ({ editorRef, disabled }) => {
  const [colorOpen, setColorOpen] = useState(false)
  const [activeFormats, setActiveFormats] = useState({})
  const colorRef = useOutsideClick(() => setColorOpen(false))

  useEffect(() => {
    const update = () =>
      setActiveFormats({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      })
    document.addEventListener("selectionchange", update)
    return () => document.removeEventListener("selectionchange", update)
  }, [])

  const exec = (cmd, val) => {
    if (disabled) return
    editorRef.current?.focus()
    document.execCommand(cmd, false, val)
  }

  const handleLink = () => {
    if (disabled) return
    const url = prompt("Enter URL:")
    if (url) exec("createLink", url.startsWith("http") ? url : `https://${url}`)
  }

  const handleImage = () => {
    if (disabled) return
    const url = prompt("Enter image URL:")
    if (url) exec("insertImage", url)
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        padding: "6px 14px",
        borderBottom: "1px solid #f3f4f6",
        background: "#fafafa",
        flexWrap: "wrap",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <ToolbarButton
        title="Bold"
        onClick={() => exec("bold")}
        active={activeFormats.bold}
      >
        <Bold size={13} strokeWidth={2.5} />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        onClick={() => exec("italic")}
        active={activeFormats.italic}
      >
        <Italic size={13} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        onClick={() => exec("underline")}
        active={activeFormats.underline}
      >
        <Underline size={13} strokeWidth={2} />
      </ToolbarButton>

      <div ref={colorRef} style={{ position: "relative", display: "flex" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <ToolbarButton
            title="Font color"
            onClick={() => !disabled && setColorOpen((v) => !v)}
          >
            <span
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: "#374151",
                  fontFamily: "serif",
                }}
              >
                A
              </span>
              <span
                style={{
                  width: 12,
                  height: 3,
                  borderRadius: 1,
                  background: "#111827",
                }}
              />
            </span>
          </ToolbarButton>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              if (!disabled) setColorOpen((v) => !v)
            }}
            style={{
              width: 14,
              height: 28,
              border: "none",
              background: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
            }}
          >
            <ChevronDown size={9} strokeWidth={2.5} color="#9ca3af" />
          </button>
        </div>
        {colorOpen && !disabled && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              padding: 8,
              zIndex: 600,
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 4,
            }}
          >
            {FONT_COLORS.map((c) => (
              <ColorSwatch
                key={c}
                color={c}
                onSelect={(col) => {
                  exec("foreColor", col)
                  setColorOpen(false)
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          width: 1,
          height: 18,
          background: "#e5e7eb",
          margin: "0 4px",
          flexShrink: 0,
        }}
      />

      <ToolbarButton title="Link" onClick={handleLink}>
        <Link size={13} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton title="Image" onClick={handleImage}>
        <Image size={13} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton title="Code" onClick={() => exec("formatBlock", "pre")}>
        <Code size={13} strokeWidth={2} />
      </ToolbarButton>

      <div
        style={{
          width: 1,
          height: 18,
          background: "#e5e7eb",
          margin: "0 4px",
          flexShrink: 0,
        }}
      />

      <ToolbarButton title="More">
        <span style={{ display: "flex", gap: 2 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 3,
                height: 3,
                borderRadius: "50%",
                background: "#6b7280",
                display: "block",
              }}
            />
          ))}
        </span>
      </ToolbarButton>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────

const EmailLeadMany = ({
  isOpen = true,
  onClose = () => {},
  recipients = [], // [{ applicantId, name, email, context }]
  organizationId = "",
  organizationName = "",
  jobId = "",
  confirmLabel = "Send", // ← add
  onSendComplete = null, // ← add
}) => {
  const templates = useEmailTemplates()

  const DOMAIN = "@resolut.co.in"
  const DEFAULT_PREFIX = "communications"

  // ── Page 1 state ──
  const [page, setPage] = useState(1)
  const [fromPrefix, setFromPrefix] = useState(DEFAULT_PREFIX)
  const [fromDraft, setFromDraft] = useState(DEFAULT_PREFIX)
  const [isEditingFrom, setIsEditingFrom] = useState(false)
  const [excludeActive, setExcludeActive] = useState(false)
  const [templateOpen, setTemplateOpen] = useState(false)

  // subject/body shown in editor — may be resolved against recipient[0] for preview
  const [subject, setSubject] = useState("")

  // ── KEY FIX: raw refs always hold the unresolved template ──────────────────
  // These are what get resolved per-recipient on Preview click.
  // The editor and subject input show a preview resolved against recipient[0]
  // so the user sees realistic content, but we never use that for bulk resolving.
  const rawSubjectRef = useRef("") // unresolved template string
  const rawBodyRef = useRef("") // unresolved template HTML

  // ── Page 2 state ──
  const [previewIndex, setPreviewIndex] = useState(0)
  const [resolvedEmails, setResolvedEmails] = useState([])
  const [isSending, setIsSending] = useState(false)

  // ── Animation ──
  const [visible, setVisible] = useState(false)

  const editorRef = useRef(null)
  const subjectRef = useRef(null)
  const fromInputRef = useRef(null)
  const templateRef = useOutsideClick(() => setTemplateOpen(false))

  const n = recipients.length
  const firstCtx = recipients[0]?.context ?? null

  useEffect(() => {
    if (isOpen) {
      const t = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(t)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && page === 1) setTimeout(() => subjectRef.current?.focus(), 120)
  }, [isOpen, page])

  useEffect(() => {
    if (isEditingFrom) setTimeout(() => fromInputRef.current?.focus(), 50)
  }, [isEditingFrom])

  useEffect(() => {
    const onKey = (e) => {
      if (!isOpen) return
      if (e.key === "Escape" && !isSending) {
        if (isEditingFrom) {
          setIsEditingFrom(false)
          setFromDraft(fromPrefix)
          return
        }
        if (page === 2) {
          setPage(1)
          return
        }
        handleClose()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault()
        if (page === 1 && canPreview) handlePreview()
        if (page === 2 && !isSending) handleSend()
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [isOpen, isSending, page, isEditingFrom, fromPrefix, subject])

  const handleClose = () => {
    setVisible(false)
    setTimeout(onClose, 200)
  }

  const handleFromSave = () => {
    const trimmed = fromDraft.trim().replace(/[^a-zA-Z0-9._+-]/g, "")
    if (trimmed) setFromPrefix(trimmed)
    else setFromDraft(fromPrefix)
    setIsEditingFrom(false)
  }

  const canPreview =
    subject.trim().length > 0 &&
    getTextContent(rawBodyRef.current).trim().length > 0

  // ── Template selection ────────────────────────────────────────────────────
  // Store raw template in refs, show recipient[0]-resolved preview in editor
  const handleSelectTemplate = (tpl) => {
    // 1. Save raw unresolved strings
    rawSubjectRef.current = tpl.subject ?? ""
    rawBodyRef.current = tpl.body ? tpl.body.replace(/\n/g, "<br>") : ""

    // 2. Show resolved-against-recipient[0] in the editor as a preview
    const previewSubject = resolvePlaceholders(rawSubjectRef.current, firstCtx)
    const previewBody = resolvePlaceholders(rawBodyRef.current, firstCtx)

    setSubject(previewSubject)
    if (editorRef.current) {
      editorRef.current.innerHTML = previewBody
    }

    setTemplateOpen(false)
    setTimeout(() => editorRef.current?.focus(), 50)
  }

  // ── Manual edits sync raw refs too ───────────────────────────────────────
  // When the user types directly (no template), their content IS the raw template.
  // We keep rawBodyRef in sync so Preview always has the latest version.
  const handleEditorInput = () => {
    if (!editorRef.current) return
    rawBodyRef.current = editorRef.current.innerHTML
  }

  const handleSubjectChange = (e) => {
    const val = e.target.value
    setSubject(val)
    rawSubjectRef.current = val
  }

  // ── Preview: resolve raw refs per recipient ───────────────────────────────
  const handlePreview = () => {
    const resolved = recipients.map((r) => ({
      recipient: r,
      subject: resolvePlaceholders(rawSubjectRef.current, r.context),
      body: resolvePlaceholders(rawBodyRef.current, r.context),
    }))
    setResolvedEmails(resolved)
    setPreviewIndex(0)
    setPage(2)
  }

  // ── Send ──────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    setIsSending(true)
    try {
      await Promise.all(
        resolvedEmails.map(({ recipient, subject: s, body: b }) =>
          sendLeadEmail(organizationId, recipient.applicantId, {
            text: b.trim(),
            subject: s.trim(),
            html: b.trim(),
            candidateEmail: recipient.email,
            organizationName: organizationName,
            jobId: jobId,
          }),
        ),
      )
      toast.success(`${n} emails sent!`)
      if (onSendComplete) {
        onSendComplete()
      } else {
        handleClose()
      }
    } catch (err) {
      console.error(err)
      toast.error("Failed to send some emails.")
    } finally {
      setIsSending(false)
    }
  }

  if (!isOpen) return null

  const current = resolvedEmails[previewIndex] ?? null

  const modalStyle = {
    width: "100%",
    maxWidth: 620,
    background: "#fff",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    fontFamily: ff,
    pointerEvents: "all",
    opacity: visible ? 1 : 0,
    transform: visible
      ? "translateY(0) scale(1)"
      : "translateY(6px) scale(0.99)",
    transition:
      "opacity 0.18s ease, transform 0.18s cubic-bezier(0.22,1,0.36,1)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        .emm-editor { font-family: ${ff}; }
        .emm-editor:empty::before { content: attr(data-placeholder); color: #9ca3af; pointer-events: none; }
        .emm-editor:focus { outline: none; }
        .emm-editor p { margin: 0.35em 0; }
        .emm-editor ul { margin: 0.4em 0; padding-left: 1.4em; list-style: disc; }
        .emm-editor ol { margin: 0.4em 0; padding-left: 1.4em; list-style: decimal; }
        .emm-editor li { display: list-item; margin: 0.15em 0; }
        .emm-tpl-btn:hover { background: #f3f4f6 !important; }
        .emm-close-btn:hover { color: #374151 !important; background: #f3f4f6 !important; }
        .emm-from-edit:hover { background: #f3f4f6 !important; }
        .emm-nav-btn:hover:not(:disabled) { background: #f3f4f6 !important; }
        .emm-goback:hover { color: #111827 !important; }
        @keyframes emm-spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={!isSending ? handleClose : undefined}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 200,
          background: "rgba(0,0,0,0.28)",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.18s ease",
        }}
      />

      {/* Wrapper */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 201,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 16px",
          pointerEvents: "none",
        }}
      >
        <div onClick={(e) => e.stopPropagation()} style={modalStyle}>
          {/* ══════════════════ PAGE 1 — COMPOSE ══════════════════ */}
          {page === 1 && (
            <>
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  padding: "16px 20px",
                  borderBottom: divider,
                }}
              >
                <div>
                  <p
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#111827",
                      margin: 0,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Send Email
                  </p>
                  <p
                    style={{
                      fontSize: 13,
                      color: "#6b7280",
                      margin: "3px 0 0",
                      fontWeight: 500,
                    }}
                  >
                    Sending to{" "}
                    <span style={{ fontWeight: 600, color: "#374151" }}>
                      {n} candidate{n !== 1 ? "s" : ""}
                    </span>
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="emm-close-btn"
                  style={{
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "none",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    color: "#6b7280",
                    transition: "all 0.12s",
                  }}
                >
                  <X size={14} strokeWidth={2} />
                </button>
              </div>

              {/* Exclude checkbox */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 20px",
                  borderBottom: divider,
                }}
              >
                <input
                  type="checkbox"
                  id="emm-excl"
                  checked={excludeActive}
                  onChange={(e) => setExcludeActive(e.target.checked)}
                  style={{
                    width: 14,
                    height: 14,
                    cursor: "pointer",
                    accentColor: "#111827",
                    flexShrink: 0,
                  }}
                />
                <label
                  htmlFor="emm-excl"
                  style={{
                    fontSize: 13,
                    color: "#374151",
                    cursor: "pointer",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    userSelect: "none",
                  }}
                >
                  Don't email candidates with other active opportunities
                  <span
                    title="Candidates with open opportunities in other pipelines will be skipped"
                    style={{
                      width: 15,
                      height: 15,
                      border: "1.5px solid #9ca3af",
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      color: "#6b7280",
                      cursor: "help",
                      flexShrink: 0,
                      fontWeight: 600,
                    }}
                  >
                    i
                  </span>
                </label>
              </div>

              {/* From row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 20px",
                  height: 46,
                  borderBottom: divider,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    width: 76,
                    flexShrink: 0,
                  }}
                >
                  From
                </span>
                <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                  {isEditingFrom ? (
                    <input
                      ref={fromInputRef}
                      value={fromDraft}
                      onChange={(e) =>
                        setFromDraft(
                          e.target.value.replace(/[^a-zA-Z0-9._+-]/g, ""),
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleFromSave()
                        if (e.key === "Escape") {
                          setIsEditingFrom(false)
                          setFromDraft(fromPrefix)
                        }
                      }}
                      onBlur={handleFromSave}
                      style={{
                        border: "none",
                        outline: "none",
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#111827",
                        background: "transparent",
                        fontFamily: ff,
                        padding: 0,
                        minWidth: 0,
                        width: `${Math.max(fromDraft.length, 4)}ch`,
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#111827",
                      }}
                    >
                      {fromPrefix}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#6b7280",
                      flexShrink: 0,
                    }}
                  >
                    {DOMAIN}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setFromDraft(fromPrefix)
                    setIsEditingFrom(true)
                  }}
                  className="emm-from-edit"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "4px 8px",
                    flexShrink: 0,
                    background: "none",
                    border: "none",
                    borderRadius: 5,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#6b7280",
                    cursor: "pointer",
                    fontFamily: ff,
                    transition: "all 0.12s",
                  }}
                >
                  <Pencil size={11} strokeWidth={2} />
                  Edit
                </button>
              </div>

              {/* Subject row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 20px",
                  height: 46,
                  borderBottom: divider,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    width: 76,
                    flexShrink: 0,
                  }}
                >
                  Subject
                </span>
                <input
                  ref={subjectRef}
                  value={subject}
                  onChange={handleSubjectChange}
                  placeholder="Write a subject…"
                  style={{
                    flex: 1,
                    border: "none",
                    outline: "none",
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#111827",
                    background: "transparent",
                    fontFamily: ff,
                    padding: 0,
                  }}
                />
              </div>

              {/* Toolbar */}
              <EditorToolbar editorRef={editorRef} disabled={false} />

              {/* Editor */}
              <div
                ref={editorRef}
                contentEditable
                onInput={handleEditorInput}
                className="emm-editor"
                data-placeholder="Write your message… Use {{candidate.firstName}} for personalisation"
                suppressContentEditableWarning
                style={{
                  minHeight: 240,
                  padding: "16px 20px",
                  fontSize: 14,
                  lineHeight: 1.75,
                  color: "#111827",
                  fontFamily: ff,
                }}
              />

              {/* Footer */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 20px",
                  borderTop: divider,
                  background: "#fff",
                }}
              >
                {/* Template picker */}
                <div ref={templateRef} style={{ position: "relative" }}>
                  <button
                    type="button"
                    className="emm-tpl-btn"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      setTemplateOpen((v) => !v)
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      height: 32,
                      padding: "0 11px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 6,
                      background: "none",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#374151",
                      cursor: "pointer",
                      fontFamily: ff,
                      transition: "background 0.12s",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <rect
                        x="1"
                        y="1"
                        width="6"
                        height="6"
                        rx="1.5"
                        fill="#6b7280"
                      />
                      <rect
                        x="9"
                        y="1"
                        width="6"
                        height="2.5"
                        rx="1"
                        fill="#6b7280"
                      />
                      <rect
                        x="9"
                        y="5.5"
                        width="6"
                        height="1.5"
                        rx="0.75"
                        fill="#9ca3af"
                      />
                      <rect
                        x="1"
                        y="9"
                        width="14"
                        height="2"
                        rx="1"
                        fill="#9ca3af"
                      />
                      <rect
                        x="1"
                        y="13"
                        width="9"
                        height="2"
                        rx="1"
                        fill="#9ca3af"
                      />
                    </svg>
                    Template
                    <ChevronDown
                      size={10}
                      strokeWidth={2.5}
                      style={{
                        marginLeft: 1,
                        transform: templateOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.15s",
                      }}
                    />
                  </button>
                  {templateOpen && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "calc(100% + 6px)",
                        left: 0,
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        boxShadow:
                          "0 -4px 20px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.06)",
                        zIndex: 500,
                        minWidth: 200,
                        overflow: "hidden",
                      }}
                    >
                      {templates.length === 0 ? (
                        <div
                          style={{
                            padding: "10px 14px",
                            fontSize: 13.5,
                            color: "#6b7280",
                            fontWeight: 500,
                          }}
                        >
                          No templates yet
                        </div>
                      ) : (
                        templates.map((t, i) => (
                          <button
                            key={t.id ?? i}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault()
                              handleSelectTemplate(t)
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              padding: "9px 14px",
                              border: "none",
                              borderBottom: "1px solid #f9fafb",
                              background: "none",
                              fontSize: 13.5,
                              fontWeight: 600,
                              color: "#111827",
                              cursor: "pointer",
                              fontFamily: ff,
                              transition: "background 0.1s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f9fafb")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "none")
                            }
                          >
                            {t.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <button
                    onClick={handleClose}
                    style={{
                      background: "none",
                      border: "1px solid #e5e7eb",
                      borderRadius: 7,
                      padding: "7px 16px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#374151",
                      cursor: "pointer",
                      fontFamily: ff,
                      transition: "all 0.12s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#9ca3af"
                      e.currentTarget.style.color = "#111827"
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e7eb"
                      e.currentTarget.style.color = "#374151"
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePreview}
                    disabled={!canPreview}
                    style={{
                      height: 36,
                      padding: "0 20px",
                      background: canPreview ? "#111827" : "#f3f4f6",
                      border: "none",
                      borderRadius: 7,
                      fontSize: 13,
                      fontWeight: 600,
                      color: canPreview ? "#fff" : "#9ca3af",
                      cursor: canPreview ? "pointer" : "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontFamily: ff,
                      letterSpacing: "-0.01em",
                      transition: "background 0.12s, color 0.12s",
                    }}
                    onMouseEnter={(e) => {
                      if (canPreview)
                        e.currentTarget.style.background = "#27272a"
                    }}
                    onMouseLeave={(e) => {
                      if (canPreview)
                        e.currentTarget.style.background = canPreview
                          ? "#111827"
                          : "#f3f4f6"
                    }}
                  >
                    Preview
                    <ChevronRight size={13} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ══════════════════ PAGE 2 — PREVIEW ══════════════════ */}
          {page === 2 && current && (
            <>
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "14px 20px",
                  borderBottom: divider,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    className="emm-nav-btn"
                    onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                    disabled={previewIndex === 0}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      height: 28,
                      padding: "0 8px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 6,
                      background: "none",
                      fontSize: 12,
                      fontWeight: 600,
                      color: previewIndex === 0 ? "#d1d5db" : "#374151",
                      cursor: previewIndex === 0 ? "not-allowed" : "pointer",
                      fontFamily: ff,
                      transition: "background 0.12s",
                    }}
                  >
                    <ChevronLeft size={12} strokeWidth={2.5} /> Prev
                  </button>

                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#374151",
                      padding: "0 4px",
                      fontFamily: ff,
                    }}
                  >
                    {previewIndex + 1} of {n}
                  </span>

                  <button
                    className="emm-nav-btn"
                    onClick={() =>
                      setPreviewIndex((i) => Math.min(n - 1, i + 1))
                    }
                    disabled={previewIndex === n - 1}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      height: 28,
                      padding: "0 8px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 6,
                      background: "none",
                      fontSize: 12,
                      fontWeight: 600,
                      color: previewIndex === n - 1 ? "#d1d5db" : "#374151",
                      cursor:
                        previewIndex === n - 1 ? "not-allowed" : "pointer",
                      fontFamily: ff,
                      transition: "background 0.12s",
                    }}
                  >
                    Next <ChevronRight size={12} strokeWidth={2.5} />
                  </button>
                </div>

                <button
                  onClick={handleClose}
                  className="emm-close-btn"
                  style={{
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "none",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    color: "#6b7280",
                    transition: "all 0.12s",
                  }}
                >
                  <X size={14} strokeWidth={2} />
                </button>
              </div>

              {/* To — read-only chip */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 20px",
                  minHeight: 46,
                  borderBottom: divider,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    width: 76,
                    flexShrink: 0,
                  }}
                >
                  To
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                    padding: "8px 0",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "3px 10px",
                      background: "#f3f4f6",
                      borderRadius: 20,
                      fontSize: 13,
                      fontFamily: ff,
                    }}
                  >
                    {current.recipient.name && (
                      <span style={{ fontWeight: 600, color: "#111827" }}>
                        {current.recipient.name}
                      </span>
                    )}
                    <span style={{ color: "#6b7280", fontWeight: 500 }}>
                      {current.recipient.email}
                    </span>
                  </span>
                </div>
              </div>

              {/* From — read-only */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 20px",
                  height: 46,
                  borderBottom: divider,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    width: 76,
                    flexShrink: 0,
                  }}
                >
                  From
                </span>
                <span
                  style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}
                >
                  {fromPrefix}
                </span>
                <span
                  style={{ fontSize: 14, fontWeight: 500, color: "#6b7280" }}
                >
                  {DOMAIN}
                </span>
              </div>

              {/* Subject — read-only, fully resolved for this recipient */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 20px",
                  minHeight: 46,
                  borderBottom: divider,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    width: 76,
                    flexShrink: 0,
                  }}
                >
                  Subject
                </span>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#111827",
                    fontFamily: ff,
                  }}
                >
                  {current.subject}
                </span>
              </div>

              {/* Toolbar — visual only */}
              <EditorToolbar editorRef={{ current: null }} disabled />

              {/* Body — resolved HTML for this specific recipient, read-only */}
              <div
                dangerouslySetInnerHTML={{ __html: current.body }}
                style={{
                  minHeight: 240,
                  padding: "16px 20px",
                  fontSize: 14,
                  lineHeight: 1.75,
                  color: "#111827",
                  fontFamily: ff,
                  background: "#f9fafb",
                  overflowY: "auto",
                }}
              />

              {/* Footer */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 20px",
                  borderTop: divider,
                  background: "#fff",
                }}
              >
                <button
                  onClick={() => setPage(1)}
                  className="emm-goback"
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#6b7280",
                    cursor: "pointer",
                    fontFamily: ff,
                    textDecoration: "underline",
                    textUnderlineOffset: 3,
                    transition: "color 0.12s",
                    padding: 0,
                  }}
                >
                  ← Go back
                </button>

                <button
                  onClick={handleSend}
                  disabled={isSending}
                  style={{
                    height: 36,
                    padding: "0 20px",
                    background: isSending ? "#f3f4f6" : "#111827",
                    border: "none",
                    borderRadius: 7,
                    fontSize: 13,
                    fontWeight: 600,
                    color: isSending ? "#9ca3af" : "#fff",
                    cursor: isSending ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontFamily: ff,
                    letterSpacing: "-0.01em",
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSending) e.currentTarget.style.background = "#27272a"
                  }}
                  onMouseLeave={(e) => {
                    if (!isSending) e.currentTarget.style.background = "#111827"
                  }}
                >
                  {isSending ? (
                    <>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          border: "1.5px solid rgba(255,255,255,0.3)",
                          borderTopColor: "#fff",
                          borderRadius: "50%",
                          animation: "emm-spin 0.7s linear infinite",
                        }}
                      />
                      Sending…
                    </>
                  ) : (
                    `${confirmLabel} (${n})`
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default EmailLeadMany
